/**
 * Where effects come from besides potions: food, drinks, and mob hits. DOM-free.
 *
 *   applyFoodEffects(set, itemId, dmg, rnd)   run after the hunger bar was filled
 *   drinkResult(itemId)                       item left in the slot (bucket, bottle, bowl) or 0
 *   mobHitEffects(mobType)                    effects a melee hit from that mob applies to the player
 */

import { BLOCKS } from '../../blocks';
import { I } from '../itemIds';
import { FX, type EffectSet } from './effects';

export interface FoodEffect { id: number; amp: number; dur: number; chance: number }
const E = (id: number, amp: number, dur: number, chance = 1): FoodEffect => ({ id, amp, dur, chance });

export const FOOD_EFFECTS: Record<number, FoodEffect[]> = {
  [I.ROTTEN_FLESH]: [E(FX.HUNGER, 0, 600, 0.8)],
  [I.CHICKEN]: [E(FX.HUNGER, 0, 600, 0.3)],
  [I.SPIDER_EYE]: [E(FX.POISON, 0, 100)],
  // vanilla: Poison II 60 s, Hunger III 15 s, Nausea I 15 s
  [I.PUFFERFISH]: [E(FX.POISON, 1, 1200), E(FX.HUNGER, 2, 300), E(FX.NAUSEA, 0, 300)],
  // vanilla: Poison I 5 s with a 60 % chance
  [I.POISONOUS_POTATO]: [E(FX.POISON, 0, 100, 0.6)],
  [I.GOLDEN_APPLE]: [E(FX.REGENERATION, 1, 100), E(FX.ABSORPTION, 0, 2400)],
  [I.ENCHANTED_GOLDEN_APPLE]: [
    E(FX.REGENERATION, 1, 400), E(FX.RESISTANCE, 0, 6000), E(FX.FIRE_RESISTANCE, 0, 6000), E(FX.ABSORPTION, 3, 2400),
  ],
};

/** Suspicious stew: the stack's `dmg` picks the flower it was cooked with. */
export const STEW_FLOWERS: ReadonlyArray<{ block: string; fx: FoodEffect }> = [
  { block: 'Poppy', fx: E(FX.NIGHT_VISION, 0, 100) },
  { block: 'Allium', fx: E(FX.FIRE_RESISTANCE, 0, 80) },
  { block: 'Oxeye Daisy', fx: E(FX.REGENERATION, 0, 160) },
  { block: 'Cornflower', fx: E(FX.JUMP_BOOST, 0, 120) },
  { block: 'Lily of the Valley', fx: E(FX.POISON, 0, 240) },
  { block: 'Azure Bluet', fx: E(FX.BLINDNESS, 0, 160) }, // vanilla: Blindness 8 s (Weakness belongs to the tulips)
  // appended (dmg is persisted): the four tulips, Weakness 7 s
  { block: 'Red Tulip', fx: E(FX.WEAKNESS, 0, 140) },
  { block: 'Orange Tulip', fx: E(FX.WEAKNESS, 0, 140) },
  { block: 'White Tulip', fx: E(FX.WEAKNESS, 0, 140) },
  { block: 'Pink Tulip', fx: E(FX.WEAKNESS, 0, 140) },
];
/** Block id of the flower behind stew variant `dmg`, or -1. */
export const stewFlowerBlock = (dmg: number): number => {
  const name = STEW_FLOWERS[dmg]?.block;
  return name ? (BLOCKS.find((b) => b?.name === name)?.id ?? -1) : -1;
};

/** Apply the side effects of eating / drinking `itemId`. Milk and honey are handled here too. */
export function applyFoodEffects(set: EffectSet, itemId: number, dmg: number, rnd: () => number = Math.random): void {
  if (itemId === I.MILK_BUCKET) { set.clear(); return; }
  if (itemId === I.HONEY_BOTTLE) { set.remove(FX.POISON); return; }
  const list = itemId === I.SUSPICIOUS_STEW ? (STEW_FLOWERS[dmg] ? [STEW_FLOWERS[dmg].fx] : []) : FOOD_EFFECTS[itemId];
  if (!list) return;
  for (const f of list) if (f.chance >= 1 || rnd() < f.chance) set.add(f.id, f.amp, f.dur);
}

/** Ticks of holding right mouse to finish `itemId`: 32 (1.6 s), the honey bottle 40 (2 s, vanilla `drink_duration`). */
export const eatTicksOf = (itemId: number): number => (itemId === I.HONEY_BOTTLE ? 40 : 32);

/** Items that are drunk / eaten via the potion path regardless of the hunger bar. */
export const isDrink = (id: number): boolean => id === I.MILK_BUCKET;

/** What stays behind after consuming `itemId` (0 = nothing; stews and honey return their container). */
export function drinkResult(itemId: number): number {
  switch (itemId) {
    case I.MILK_BUCKET: return I.BUCKET;
    case I.HONEY_BOTTLE: case I.POTION: return I.GLASS_BOTTLE;
    case I.SUSPICIOUS_STEW: return I.BOWL;
    default: return 0;
  }
}

/** Effects a melee hit from a mob type inflicts on the player (vanilla Normal difficulty durations). */
const HIT_EFFECTS: Record<string, FoodEffect[]> = {
  wither_skeleton: [E(FX.WITHER, 0, 200)],
  cave_spider: [E(FX.POISON, 0, 140)],
  husk: [E(FX.HUNGER, 0, 140)],
  stray: [],
};
export function mobHitEffects(type: string): FoodEffect[] { return HIT_EFFECTS[type] ?? []; }

/** Arrow effects: a stray's arrow slows for 30 s. */
export const STRAY_ARROW_EFFECT: FoodEffect = E(FX.SLOWNESS, 0, 600);
